/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * Auteur: Jelmer Jellema - Spin in het Web B.V
 * Gegenereerd met Claude Code (AI), 06-07-2026
 *
 * Naam-invoer bij een node. Vervangt de qtip-gebaseerde `createNodeLabelinput`
 * uit `cytocanvas.modelview.controller.js`.
 *
 * De invoer opent op twee momenten, mits de "baas" (`DlModelCanvasComponent`) zegt dat de naam bewerkt
 * mag worden: bij een node die deze client zelf net geplaatst heeft (markering `NET_GEPLAATST` van
 * `DlModel`), en bij een klik op het label van de node (een verzoek van het canvas). Een gewone selectie
 * met een klik op het lichaam van de node opent hem niet: die geeft het nodemenu. Twee modi:
 * een `select` met normnamen wanneer het model een norm met namen heeft, anders
 * een vrije tekst-`input`. Tijdens het bewerken wordt het door Cytoscape
 * getekende label verborgen via de class `nolabel`. Opslaan en de mag-check
 * lopen via het canvas; het opslaan gaat daar als modelactie naar de samenwerk-baas.
 *
 * Zodra de invoer opent, meldt het label dat bij het canvas: het nodemenu en de prullenbak van de
 * node blijven dan weg, ook als het bewerken al is afgerond. Ze komen terug bij een klik op het lichaam
 * van de (nog geselecteerde) node, en bij een nieuwe selectie; staat de invoer dan nog open, dan legt
 * die klik hem eerst vast en sluit hem.
 *
 * Uitzondering: een node met de naam midden op het element (`labelPosition: 'center'`, de configuratie)
 * is vooral zijn naam, dus elke klik is daar een klik op het label. Daar opent elke selectie de invoer,
 * en blijven het nodemenu en de prullenbak erbij staan (besluit gebruiker 27-10-2026: een "tweede klik
 * voor de naam" zou een verborgen functie zijn).
 */
import {ChangeDetectionStrategy, Component, effect, ElementRef, inject, signal, untracked, viewChild} from '@angular/core';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import type {EventObject} from 'cytoscape';
import {DlNodeVolgerDirective} from '../../../directives/dl-node-volger/dl-node-volger.directive';
import {NET_GEPLAATST} from '../../../app/model/dl-model';
import {DlModelCanvasComponent} from '../../canvas/dl-modelcanvas/dl-modelcanvas.component';
import {BaseNodeGekoppeldComponent} from '../base-node-gekoppeld.component';
import {elements} from 'dynalearn-common/lib/elements';
import {specialenaam} from '../../../lib/specialenaam';

/**
 * Eén keuze in de norm-`select`: de op te slaan waarde en het (vertaalde) label.
 */
interface NormOptie {
    waarde: string;
    label: string;
}

@Component({
    selector: 'dl-node-label',
    standalone: true,
    imports: [DlNodeVolgerDirective, TranslateModule],
    templateUrl: './dl-node-label.component.html',
    styleUrls: ['./dl-node-label.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DlNodeLabelComponent extends BaseNodeGekoppeldComponent {

    /**
     * Referentie naar het invoerveld (input of select), zodra het gerenderd is.
     */
    private readonly invoerVeld = viewChild<ElementRef<HTMLInputElement | HTMLSelectElement>>('invoer');

    /**
     * Of de invoer op dit moment getoond wordt. Gezet bij het plaatsen van de node of bij een klik op
     * het label (mits mag), gewist bij deselectie, na bevestigen of bij een klik op het lichaam.
     */
    protected readonly bewerken = signal(false);

    /**
     * De huidig ingevoerde waarde.
     */
    protected readonly waarde = signal('');

    /**
     * De normnamen voor de `select`, of `null` in vrije invoer-modus.
     */
    protected readonly normOpties = signal<NormOptie[] | null>(null);

    /**
     * Het canvas waarbinnen dit label leeft. Levert de mag-check, de normnamen
     * en de opslag-actie. Geïnjecteerd als host-ancestor; geen extra input nodig.
     */
    private readonly modelcanvas = inject(DlModelCanvasComponent, {host: true});

    private readonly translate = inject(TranslateService);

    /**
     * De naam die in de node stond toen de bewerkmodus openging. Dient als
     * ijkpunt: wijkt de naam in de node hiervan af, dan heeft iemand anders hem
     * gewijzigd; en is de invoer hier nog gelijk aan, dan heeft de gebruiker
     * zelf niets ingevoerd.
     */
    private geopendeNaam = '';

    /**
     * Reageert op elke datawijziging van de node tijdens het bewerken. Staat er
     * een andere naam dan waarmee we openden, dan komt die van buiten — van een
     * samenwerkingspartner — en breken we het bewerken af zonder op te slaan.
     */
    private readonly onDataWijziging = (): void => {
        if ((this.node.data('nodename') || '') !== this.geopendeNaam) {
            this.afbreekBewerken();
        }
    };

    /**
     * Legt de invoer vast en sluit het veld, op verzoek van het canvas (`rondBewerkingAf`). Een vaste
     * referentie, zodat hij bij het sluiten weer afgemeld kan worden.
     */
    private readonly sluitVanBuiten = (): void => this.sluitBewerken();

    /**
     * Reageert op een klik op het lichaam van de node terwijl hij geselecteerd is: een openstaande invoer
     * wordt vastgelegd en gesloten, en het nodemenu en de prullenbak komen terug. Een klik op het label
     * telt niet: dat is een verzoek om de invoer (zie het canvas). Een vaste referentie, zodat hij weer
     * afgemeld kan worden.
     */
    private readonly opKlik = (event: EventObject): void => {
        if (this.modelcanvas.isLabelKlik(this.node, event.position)) {
            return;
        }
        this.sluitBewerken();
        this.modelcanvas.zetLabelBewerkt(this.node, false);
    };

    constructor() {
        super();

        // Volg de selectie en de mag-check. Bij deselectie, of als het bewerken niet meer mag, gaat de
        // invoer dicht: valt de verbinding weg terwijl het invoerveld openstaat, dan hoort het veld te
        // sluiten in plaats van te blijven staan (bevinding a6.15, 02-09-2026). Openen doet dit effect
        // bij een node die deze client net geplaatst heeft (de markering gaat er bij de eerste selectie
        // af), en bij elke selectie van een node met de naam in het midden. Het openen en sluiten zelf
        // blijft untracked, zodat de signals die daarin geschreven worden het effect niet opnieuw
        // aanzwengelen.
        effect(() => {
            const geselecteerd = this.geselecteerd();
            const magBewerken = this.magBewerken();
            untracked(() => {
                if (!geselecteerd) {
                    this.stopKlikLuisteraar();
                    this.modelcanvas.zetLabelBewerkt(this.node, false);
                    this.sluitBewerken();
                    return;
                }
                const netGeplaatst = !!this.node.scratch(NET_GEPLAATST);
                this.node.removeScratch(NET_GEPLAATST);
                if (!magBewerken) {
                    this.sluitBewerken();
                } else if (netGeplaatst || this.naamInMidden) {
                    this.openBewerken();
                }
            });
        });

        // Een klik op het label van deze node opent de invoer, zodra de node (ook) geselecteerd is: de
        // tap kan vóór de selectie binnenkomen. Het verzoek wordt meteen afgenomen.
        effect(() => {
            const verzoek = this.modelcanvas.labelBewerkVerzoek();
            const geselecteerd = this.geselecteerd();
            untracked(() => {
                if (!geselecteerd || verzoek !== this.node?.id()) {
                    return;
                }
                this.modelcanvas.wisLabelBewerkVerzoek();
                if (this.magBewerken()) {
                    this.openBewerken();
                }
            });
        });

        // Zet focus (en selecteer de tekst) zodra het invoerveld verschijnt.
        effect(() => {
            const el = this.invoerVeld()?.nativeElement;
            if (el) {
                el.focus();
                if (el instanceof HTMLInputElement) el.select();
            }
        });
    }

    /**
     * De op te slaan waarde voor de "naam nog open"-optie. In de norm-`select`
     * staat deze optie bovenaan; de waarde is (net als in ng1) de vertaalde tekst.
     */
    protected get naamNogOpen(): string {
        return this.translate.instant('NAAM_NOG_OPEN');
    }

    /**
     * Of het label rechts van de node hoort (anders boven, gecentreerd).
     */
    protected get labelRechts(): boolean {
        return elements.getDefinition(this.node.data('type')).labelPosition === 'right';
    }

    /**
     * Of de naam midden op de node staat (`labelPosition: 'center'`, de configuratie). Zo'n node is vooral
     * zijn naam: elke selectie opent de invoer, en het nodemenu en de prullenbak blijven erbij staan.
     */
    private get naamInMidden(): boolean {
        return elements.getDefinition(this.node.data('type')).labelPosition === 'center';
    }

    /**
     * Ruimt de bewerkmodus op als het component verdwijnt terwijl er nog bewerkt
     * werd (bijvoorbeeld doordat de node uit de lijst valt). Zonder opslaan: een
     * verdwenen node is geen bevestiging.
     */
    override ngOnDestroy(): void {
        this.afbreekBewerken();
        this.stopKlikLuisteraar();
        this.modelcanvas.zetLabelBewerkt(this.node, false);
        super.ngOnDestroy();
    }

    /**
     * Bevestigt de vrije invoer (Enter): sluit de bewerkmodus, die zelf opslaat.
     */
    protected bevestigInput(): void {
        this.sluitBewerken();
    }

    /**
     * Kiest een normnaam uit de `select`: neemt de keuze over en sluit de
     * bewerkmodus, die zelf opslaat.
     */
    protected kiesNorm(nieuweWaarde: string): void {
        this.waarde.set(nieuweWaarde);
        this.sluitBewerken();
    }

    /**
     * Leest of de naam van de gekoppelde node op dit moment bewerkt mag worden.
     */
    private magBewerken(): boolean {
        return !!this.node && this.modelcanvas.magNodeLabelBewerken(this.node);
    }

    /**
     * Start de bewerkmodus: verbergt het cyto-label, laadt de eventuele
     * normnamen, vult de huidige nodenaam voor, gaat de node bewaken op een
     * naamwijziging van buitenaf en meldt zich aan bij het canvas.
     *
     * Bij een norm met namen staat een naam die niet in de lijst voorkomt (zoals de standaardnaam
     * "Entiteit" van een net geplaatste node) op "nog geen naam": zo toont de keuzelijst hem, en zo
     * slaat het sluiten hem ook op, net als in AngularJS.
     */
    private openBewerken(): void {
        if (this.bewerken()) return;
        this.node.addClass('nolabel');
        const opties = this.bouwNormOpties();
        this.normOpties.set(opties);
        this.geopendeNaam = this.node.data('nodename') || '';
        const inLijst = !opties || opties.some(optie => optie.waarde === this.geopendeNaam);
        this.waarde.set(inLijst ? this.geopendeNaam : this.naamNogOpen);
        this.bewerken.set(true);
        if (!this.naamInMidden) {
            //menu en prullenbak weg tot een klik op het lichaam; bij een naam in het midden blijven ze staan
            this.modelcanvas.zetLabelBewerkt(this.node, true);
            this.startKlikLuisteraar();
        }
        this.node.on('data', this.onDataWijziging);
        this.modelcanvas.meldBewerkingOpen(this.sluitVanBuiten);
    }

    /**
     * Sluit de bewerkmodus op eigen initiatief van de gebruiker (bevestigen of
     * de node deselecteren) en slaat de ingevoerde waarde op. Geen-op als er
     * niet bewerkt werd. Eerst beëindigen, dan opslaan: zo ziet de eigen
     * schrijfactie geen bewaker meer die hem als wijziging van buiten leest.
     */
    private sluitBewerken(): void {
        if (!this.bewerken()) return;
        const ingevoerd = this.waarde();
        this.afbreekBewerken();
        this.commit(ingevoerd);
    }

    /**
     * Breekt de bewerkmodus af zonder op te slaan: bewaking eraf, afgemeld bij
     * het canvas, cyto-label weer zichtbaar, invoer weg. Dit is de route bij een
     * naamwijziging van een samenwerkingspartner en bij het verdwijnen van de
     * node — de binnengekomen naam wint dus altijd, ook als de gebruiker net aan
     * het typen was.
     */
    private afbreekBewerken(): void {
        if (!this.bewerken()) return;
        this.modelcanvas.meldBewerkingDicht(this.sluitVanBuiten);
        // cast zoals in BaseNodeGekoppeldComponent: de cytoscape-typings kennen deze
        // off-overload (events + handler, zonder selector) niet
        (this.node as any)?.off('data', this.onDataWijziging);
        this.node?.removeClass('nolabel');
        this.bewerken.set(false);
    }

    /**
     * Gaat luisteren naar een klik op de node, zodat die het nodemenu en de prullenbak kan terugbrengen.
     * Eerst afmelden, zodat een tweede keer openen binnen dezelfde selectie geen dubbele luisteraar geeft.
     */
    private startKlikLuisteraar(): void {
        this.stopKlikLuisteraar();
        this.node.on('tap', this.opKlik);
    }

    /**
     * Stopt het luisteren naar een klik op de node.
     */
    private stopKlikLuisteraar(): void {
        // cast zoals bij `onDataWijziging`: de typings kennen de off-overload zonder selector niet
        (this.node as any)?.off('tap', this.opKlik);
    }

    /**
     * Slaat een nieuwe naam op via het canvas. Alleen als hij niet leeg is, de
     * gebruiker hem zelf heeft ingevoerd (dus afwijkt van waarmee we openden) en
     * hij verschilt van wat er nu in de node staat. Die middelste voorwaarde
     * voorkomt dat het sluiten van een onaangeroerd veld de naam overschrijft
     * die een samenwerkingspartner intussen heeft gezet.
     */
    private commit(naam: string): void {
        if (naam?.length && naam !== this.geopendeNaam && naam !== this.node.data('nodename')) {
            this.modelcanvas.uitvoerWijzigNaam(this.node, naam);
        }
    }

    /**
     * Bouwt de gesorteerde lijst normopties, of `null` wanneer het model geen
     * norm met namen heeft (dan geldt vrije invoer).
     */
    private bouwNormOpties(): NormOptie[] | null {
        const namen = this.modelcanvas.getNormNamen();
        if (!namen) return null;
        return namen
            .map(n => ({waarde: n.naam, label: specialenaam(n.naam, this.translate) + (n.aantal > 1 ? ` (${n.aantal}x)` : '')}))
            .sort((a, b) => specialenaam(a.waarde, this.translate).localeCompare(specialenaam(b.waarde, this.translate)));
    }
}
